// Utilities for resolving the NFTs owned by an XRPL wallet address.
// Uses the free xrpldata.com XLS-20 API (CORS enabled, ~10 req/min).

const API_BASE = 'https://api.xrpldata.com/api/v1/xls20-nfts/owner';

// XRPL classic address: starts with 'r', base58 (no 0, O, I, l), 25-35 chars total.
const XRPL_ADDRESS_RE = /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/;

// In-memory cache of address -> Set<NFTokenID(uppercase)> so repeated searches
// for the same wallet don't spend additional API requests.
const ownerCache = new Map();

// Lightweight client-side rate limiter to stay under the API's ~10 req/min cap.
const RATE_LIMIT_MAX = 8; // leave headroom below the documented 10/min
const RATE_LIMIT_WINDOW_MS = 60 * 1000;
let requestTimestamps = [];

export function isXrplAddress(str) {
  if (typeof str !== 'string') return false;
  return XRPL_ADDRESS_RE.test(str.trim());
}

function pruneOldRequests(now) {
  requestTimestamps = requestTimestamps.filter((t) => now - t < RATE_LIMIT_WINDOW_MS);
}

// Returns the number of seconds until a request slot frees up, or 0 if allowed now.
export function getRateLimitCooldown() {
  const now = Date.now();
  pruneOldRequests(now);
  if (requestTimestamps.length < RATE_LIMIT_MAX) return 0;
  const oldest = requestTimestamps[0];
  return Math.ceil((RATE_LIMIT_WINDOW_MS - (now - oldest)) / 1000);
}

/**
 * Fetch the set of NFTokenIDs currently owned by an XRPL address.
 * @param {string} address XRPL classic address (r...)
 * @returns {Promise<Set<string>>} uppercased NFTokenIDs
 * @throws {Error} on invalid address, rate limit, or network/API failure
 */
export async function fetchOwnedNFTokenIDs(address) {
  const normalizedAddr = typeof address === 'string' ? address.trim() : '';

  if (!isXrplAddress(normalizedAddr)) {
    throw new Error('Please enter a valid XRPL wallet address.');
  }

  if (ownerCache.has(normalizedAddr)) {
    return ownerCache.get(normalizedAddr);
  }

  const cooldown = getRateLimitCooldown();
  if (cooldown > 0) {
    throw new Error(
      `Rate limit reached. Please wait ${cooldown}s before searching another wallet.`
    );
  }

  requestTimestamps.push(Date.now());

  let response;
  try {
    response = await fetch(`${API_BASE}/${encodeURIComponent(normalizedAddr)}`);
  } catch (networkError) {
    throw new Error('Network error while contacting the XRPL. Please try again.');
  }

  if (response.status === 429) {
    throw new Error('The XRPL data service is rate limiting requests. Please wait a minute.');
  }

  if (!response.ok) {
    throw new Error(`Could not load wallet NFTs (error ${response.status}). Please try again.`);
  }

  let payload;
  try {
    payload = await response.json();
  } catch (parseError) {
    throw new Error('Received an unexpected response from the XRPL data service.');
  }

  const nfts = payload && payload.data && Array.isArray(payload.data.nfts) ? payload.data.nfts : [];
  const ownedIds = new Set(
    nfts
      .map((nft) => (nft && nft.NFTokenID ? String(nft.NFTokenID).toUpperCase() : ''))
      .filter(Boolean)
  );

  ownerCache.set(normalizedAddr, ownedIds);
  return ownedIds;
}
